/**
 * @fileoverview SharedAttachmentsDialog
 *
 * Resolves a job's reusable ("custom") attachment references — e.g. a
 * "Form 505" declared on one or more precedent templates — to an uploaded job
 * document. Picking a document once writes the job-level resolution and
 * propagates it onto every precedent that uses that reference (see
 * `convex/jobs/precedents/attachmentReferences.ts`).
 *
 * This is the single place to manage shared references; the per-precedent
 * EditAttachmentsDialog still handles the fixed NOA / court-order / ROCAP slots.
 */

import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { Loader2, Paperclip } from "lucide-react";

import { api } from "~/convex/_generated/api";
import type { Id } from "~/convex/_generated/dataModel";

import { Badge, Button } from "@/ui/primitives";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";
import {
  SharedReferencePicker,
  type ReferenceCandidate,
} from "@/components/jobs/phases/day-one/SharedReferencePicker";

export function SharedAttachmentsDialog({
  jobId,
  open,
  onOpenChange,
}: {
  jobId: Id<"jobs">;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { data: references, isLoading: referencesLoading } = useQuery({
    ...convexQuery(
      api.jobs.precedents.attachmentReferences.listJobAttachmentReferences,
      { jobId },
    ),
    enabled: open,
  });

  const { data: candidates } = useQuery({
    ...convexQuery(api.jobs.precedents.attachments.listAttachableJobDocuments, {
      jobId,
    }),
    enabled: open,
  });

  const docCandidates = useMemo<ReferenceCandidate[]>(
    () =>
      (candidates ?? []).map((c) => ({
        documentId: c.documentId,
        fileName: c.fileName,
      })),
    [candidates],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Paperclip className="size-4 text-primary/40" />
            Shared attachments
          </DialogTitle>
          <DialogDescription>
            Pick an uploaded job document for each reusable attachment. Your
            choice is applied to every precedent that uses that reference.
          </DialogDescription>
        </DialogHeader>

        {referencesLoading ? (
          <div className="flex items-center justify-center py-10">
            <Loader2 className="size-5 animate-spin text-primary/40" />
          </div>
        ) : !references || references.length === 0 ? (
          <div className="rounded-md border border-border/60 bg-muted/30 px-4 py-6 text-center text-sm text-primary/60">
            No reusable attachment references are used by this job&apos;s
            precedents. Add a custom attachment to a template in Precedent
            Settings to create one.
          </div>
        ) : (
          <div className="max-h-[60vh] space-y-3 overflow-y-auto pr-1">
            {references.map((reference) => {
              return (
                <div
                  key={reference.referenceKey}
                  className="rounded-md border border-border/50 p-3"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-medium text-primary/80">
                          {reference.name}
                        </span>
                        <Badge variant="secondary" className="text-[10px]">
                          {reference.usedByCount} precedent
                          {reference.usedByCount === 1 ? "" : "s"}
                        </Badge>
                        {reference.resolution?.status === "resolved" ? (
                          <Badge className="text-[10px]">Attached</Badge>
                        ) : reference.resolution?.status === "skipped" ? (
                          <Badge variant="secondary" className="text-[10px]">
                            Skipped
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="text-[10px]">
                            Not set
                          </Badge>
                        )}
                      </div>
                      {reference.resolution?.fileName && (
                        <p className="mt-1 truncate text-xs text-primary/50">
                          {reference.resolution.fileName}
                        </p>
                      )}
                    </div>
                  </div>

                  <div className="mt-3">
                    <SharedReferencePicker
                      jobId={jobId}
                      referenceKey={reference.referenceKey}
                      name={reference.name}
                      candidates={docCandidates}
                      resolution={reference.resolution}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
